import { useMetricsCalculationsQuery } from "src/hooks/useMetricsCalculationsQuery";
import { useCurrencySymbol } from "src/hooks/useCurrencySymbol";
import { CalculateCostPerValues } from "src/utils/CalculateCostPerValues";
import { observer } from "mobx-react-lite";
import { UnitType } from "../../types/UnitType";
import { InspectorGroup } from "./InspectorGroup";
import { InspectorRow, State } from "./InspectorRow";
import { CostGroupLoading } from "./CostGroupLoading";
import { ZoneType } from "src/types/Zone";
import type { Selection } from "src/types/Selection";
import type { World } from "src/types/World";
import { useActiveParams } from "src/hooks/useActiveParams";
import { sum } from "src/service/core/data/number";
import { createCostingViewForTotal } from "src/service/cost/utility/custom-view";
import { perSqmToPerSqft } from "src/hooks/meterToFeet";
import { useTranslation } from "src/translations";

export const CostMetricsInspector = observer(
  (props: { world: World; unit: UnitType; selection: Selection }) => {
    const projectId = useActiveParams().projectId ?? "";
    const currency = useCurrencySymbol();
    const { t } = useTranslation();

    const selectedBuildings = props.selection.zones.at(0)?.buildings
      ? props.selection.zones.at(0)?.buildings
      : props.selection.buildings;

    const metricsCalculations = useMetricsCalculationsQuery(
      projectId,
      props.world,
      selectedBuildings
    );

    if (!metricsCalculations.data) return <CostGroupLoading />;
    const costingView = createCostingViewForTotal(metricsCalculations.data);

    const constructionSubTotalValue = sum([
      costingView.summary.constructionModules,
      costingView.summary.constructionFitOut,
      costingView.summary.constructionBaseBuild,
    ]);

    const calculateCostPerValues = CalculateCostPerValues(
      constructionSubTotalValue,
      props.world.architecturalMetrics.ApartmentQuantityTotal,
      props.world.architecturalMetrics.GIATotal
    );
    if (
      props.selection.zones.length > 0 &&
      props.selection.zones.every((zone) => zone.zoneType !== ZoneType.buildable)
    )
      return null;
    return (
      <InspectorGroup label={t("costMetrics.title")}>
        <InspectorRow
          currency={currency}
          state={State.FILLED}
          label={t("costMetrics.modules")}
          value={costingView.summary.constructionModules}
          decimalPoint={0}
        />
        <InspectorRow
          currency={currency}
          state={State.FILLED}
          label={t("costMetrics.fitOut")}
          value={costingView.summary.constructionFitOut}
          decimalPoint={0}
        />
        <InspectorRow
          currency={currency}
          state={State.FILLED}
          label={t("costMetrics.baseBuild")}
          value={costingView.summary.constructionBaseBuild}
          decimalPoint={0}
        />
        <InspectorRow
          currency={currency}
          state={State.FILLED}
          label={t("costMetrics.construction")}
          value={constructionSubTotalValue}
          decimalPoint={0}
        />
        <InspectorRow
          currency={currency}
          state={State.FILLED}
          label={t("costMetrics.constructionPerUnit")}
          value={calculateCostPerValues.unit}
          decimalPoint={0}
          unit={t("costMetrics.perUnit")}
        />
        <InspectorRow
          currency={currency}
          state={State.FILLED}
          label={t("costMetrics.constructionPerArea")}
          value={
            props.unit === UnitType.Metric
              ? calculateCostPerValues.sqm
              : perSqmToPerSqft(calculateCostPerValues.sqm)
          }
          decimalPoint={0}
          unit={props.unit === UnitType.Metric ? t("costMetrics.perSqm") : t("costMetrics.perSqft")}
        />
      </InspectorGroup>
    );
  }
);
